import React, { useCallback } from 'react';

interface CommissionSplitSliderProps {
  senderPercent: number;
  receiverPercent: number;
  onChange: (senderPercent: number, receiverPercent: number) => void;
  senderLabel?: string;
  receiverLabel?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export const CommissionSplitSlider: React.FC<CommissionSplitSliderProps> = ({
  senderPercent,
  receiverPercent,
  onChange,
  senderLabel = 'Your Split',
  receiverLabel = 'Receiver Split',
  disabled = false,
  error,
  className = '',
}) => {
  // Validate that percentages add up to 100
  const isValid = senderPercent + receiverPercent === 100;

  // Handle slider change
  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSenderPercent = parseInt(e.target.value, 10);
      const newReceiverPercent = 100 - newSenderPercent;
      onChange(newSenderPercent, newReceiverPercent);
    },
    [onChange]
  );

  // Handle sender input change
  const handleSenderInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = parseInt(e.target.value, 10);
      if (isNaN(value)) value = 0;
      if (value < 0) value = 0;
      if (value > 100) value = 100;
      onChange(value, 100 - value);
    },
    [onChange]
  );

  // Handle receiver input change
  const handleReceiverInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = parseInt(e.target.value, 10);
      if (isNaN(value)) value = 0;
      if (value < 0) value = 0;
      if (value > 100) value = 100;
      onChange(100 - value, value);
    },
    [onChange]
  );

  // Quick split presets
  const presets = [
    { sender: 50, receiver: 50, label: '50/50' },
    { sender: 60, receiver: 40, label: '60/40' },
    { sender: 70, receiver: 30, label: '70/30' },
    { sender: 25, receiver: 75, label: '25/75' },
  ];

  return (
    <div className={`w-full ${className}`}>
      <label className="block text-sm font-medium text-secondary-700 mb-3">
        Commission Split
      </label>

      {/* Visual Split Bar */}
      <div className="mb-4">
        <div className="flex h-8 rounded-lg overflow-hidden border border-secondary-200">
          <div
            className="bg-primary-500 flex items-center justify-center text-white text-sm font-medium transition-all duration-200"
            style={{ width: `${senderPercent}%` }}
          >
            {senderPercent > 15 && `${senderPercent}%`}
          </div>
          <div
            className="bg-emerald-500 flex items-center justify-center text-white text-sm font-medium transition-all duration-200"
            style={{ width: `${receiverPercent}%` }}
          >
            {receiverPercent > 15 && `${receiverPercent}%`}
          </div>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-primary-600 font-medium">{senderLabel}</span>
          <span className="text-xs text-emerald-600 font-medium">{receiverLabel}</span>
        </div>
      </div>

      {/* Slider */}
      <div className="mb-4">
        <input
          type="range"
          min="0"
          max="100"
          value={senderPercent}
          onChange={handleSliderChange}
          disabled={disabled}
          className={`
            w-full h-2 rounded-lg appearance-none cursor-pointer
            bg-gradient-to-r from-primary-500 to-emerald-500
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            background: `linear-gradient(to right, rgb(59 130 246) ${senderPercent}%, rgb(16 185 129) ${senderPercent}%)`,
          }}
        />
      </div>

      {/* Number Inputs */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs text-secondary-500 mb-1">{senderLabel}</label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              value={senderPercent}
              onChange={handleSenderInputChange}
              disabled={disabled}
              className={`
                block w-full rounded-lg border bg-white px-3 py-2 text-secondary-900
                text-center font-medium pr-8
                focus:outline-none focus:ring-2 focus:ring-offset-0
                ${disabled ? 'bg-secondary-50 cursor-not-allowed' : ''}
                border-secondary-300 focus:border-primary-500 focus:ring-primary-500
              `}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400">%</span>
          </div>
        </div>
        <div>
          <label className="block text-xs text-secondary-500 mb-1">{receiverLabel}</label>
          <div className="relative">
            <input
              type="number"
              min="0"
              max="100"
              value={receiverPercent}
              onChange={handleReceiverInputChange}
              disabled={disabled}
              className={`
                block w-full rounded-lg border bg-white px-3 py-2 text-secondary-900
                text-center font-medium pr-8
                focus:outline-none focus:ring-2 focus:ring-offset-0
                ${disabled ? 'bg-secondary-50 cursor-not-allowed' : ''}
                border-secondary-300 focus:border-emerald-500 focus:ring-emerald-500
              `}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400">%</span>
          </div>
        </div>
      </div>

      {/* Preset Buttons */}
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-secondary-500 self-center mr-2">Quick select:</span>
        {presets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onChange(preset.sender, preset.receiver)}
            disabled={disabled}
            className={`
              px-3 py-1 text-xs font-medium rounded-full border transition-colors
              ${senderPercent === preset.sender && receiverPercent === preset.receiver
                ? 'bg-primary-100 text-primary-700 border-primary-300'
                : 'bg-white text-secondary-600 border-secondary-300 hover:bg-secondary-50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Validation Error */}
      {!isValid && (
        <p className="mt-2 text-sm text-danger-600" role="alert">
          Commission split must equal 100%
        </p>
      )}

      {/* External Error */}
      {error && (
        <p className="mt-2 text-sm text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default CommissionSplitSlider;
